/**
 * NPC temperaments and sides (ADR-136 decisions 1 and 3): the four temperaments a builder sets, the two sides the
 * creature store holds, and the one pure table every combat, nameplate and talk site reads, so the rules live in
 * one place. Pure.
 */

/**
 * How an NPC meets the world, in the order a builder is offered them.
 */
export const NPC_TEMPERAMENTS = ["passive", "neutral", "aggressive", "combative"] as const;
export type NpcTemperament = (typeof NPC_TEMPERAMENTS)[number];

/**
 * The side a creature-store row is on: `wild` for creatures, `folk` for NPCs.
 */
export const SIDES = ["wild", "folk"] as const;
export type Side = (typeof SIDES)[number];

/**
 * The colour of an NPC's nameplate: the creature plates' white for a non-combatant, gold for a neutral, red for a
 * hostile and green for a friendly fighter.
 */
export type NpcPlate = "white" | "gold" | "red" | "green";

/**
 * When a player may start a conversation with an NPC, beyond the rule that a player in combat never may (ruling 4):
 * `unlessPlayerInCombat` always, `whileNotInCombat` while the NPC is not in combat, `notWhileThreatened` while it
 * does not hold the player in its threat ring and is not in combat.
 */
export type NpcTalkRule = "unlessPlayerInCombat" | "whileNotInCombat" | "notWhileThreatened";

/**
 * What a temperament means in play.
 */
export interface NpcPolicy {
  /** Whether the NPC has a fighter at all: Health and Level on the wire, a place in every shape query. */
  readonly fights: boolean;
  /** Whether a player may harm it. */
  readonly playersMayAttack: boolean;
  /** Whether a wild creature may harm it. */
  readonly creaturesMayAttack: boolean;
  readonly plate: NpcPlate;
  /** Whether its nameplate carries a health bar. */
  readonly healthBar: boolean;
  readonly talk: NpcTalkRule;
}

const POLICIES: Readonly<Record<NpcTemperament, NpcPolicy>> = Object.freeze({
  passive: Object.freeze({ fights: false, playersMayAttack: false, creaturesMayAttack: false, plate: "white", healthBar: false, talk: "unlessPlayerInCombat" }),
  neutral: Object.freeze({ fights: true, playersMayAttack: true, creaturesMayAttack: false, plate: "gold", healthBar: true, talk: "whileNotInCombat" }),
  aggressive: Object.freeze({ fights: true, playersMayAttack: true, creaturesMayAttack: false, plate: "red", healthBar: true, talk: "notWhileThreatened" }),
  combative: Object.freeze({ fights: true, playersMayAttack: true, creaturesMayAttack: true, plate: "green", healthBar: true, talk: "whileNotInCombat" }),
});

/**
 * Whether a value is one of the four temperaments.
 *
 * @param value Anything.
 * @returns True for a temperament's name.
 */
export function isNpcTemperament(value: unknown): value is NpcTemperament {
  return typeof value === "string" && (NPC_TEMPERAMENTS as readonly string[]).includes(value);
}

/**
 * The rules of one temperament.
 *
 * @param temperament The temperament.
 * @returns Its policy.
 */
export function npcPolicy(temperament: NpcTemperament): NpcPolicy {
  return POLICIES[temperament];
}

/**
 * The side of a fighter kind.
 *
 * @param kind The creature store's kind of fighter: `creature` or `npc`.
 * @returns `wild` for a creature, `folk` for an NPC.
 */
export function sideOfKind(kind: "creature" | "npc"): Side {
  return kind === "creature" ? "wild" : "folk";
}
